import React from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../providers/AuthProvider';
import { userHasModuleAccess, userIsTenantAdmin } from '@/lib/lmsNavRegistry';

export function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, user, creditLocked, passwordExpired } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (passwordExpired) return <Navigate to="/force-change-password" replace />;
  if (creditLocked && !user?.is_system_admin) return <Navigate to="/credit-lockout" replace />;

  return children;
}

export function TenantRoute({ children }) {
  const { user } = useAuth();
  if (user?.is_system_admin) return <Navigate to="/system/tenants" replace />;
  return children;
}

/**
 * Enforce LMS module visibility for direct URL access.
 * Module keys come from the sidebar registry (lmsNavRegistry) — do not hardcode roles.
 */
export function ModuleRoute({ module, allowedRoles = [], children }) {
  const { user } = useAuth();
  if (user?.is_system_admin) return <Navigate to="/system/tenants" replace />;
  if (module === 'system' && (user?.roles || []).includes('risk_manager')) return children;
  if (allowedRoles.length > 0 && allowedRoles.some((r) => (user?.roles || []).includes(r))) return children;
  if (!userHasModuleAccess(user, module)) return <Navigate to="/dashboard" replace />;
  return children;
}

export function TenantAdminRoute({ children }) {
  const { user } = useAuth();
  if (!userIsTenantAdmin(user)) return <Navigate to="/dashboard" replace />;
  return children;
}

export function SystemAdminRoute({ children }) {
  const { user } = useAuth();
  if (!user?.is_system_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

export function PublicOnlyRoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return user?.is_system_admin
      ? <Navigate to="/system/tenants" replace />
      : <Navigate to="/dashboard" replace />;
  }

  return children;
}

export function IndexRedirect() {
  const { user } = useAuth();
  return user?.is_system_admin ? <Navigate to="/system/tenants" replace /> : <Navigate to="/dashboard" replace />;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

TenantRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

ModuleRoute.propTypes = {
  module: PropTypes.string.isRequired,
  allowedRoles: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node.isRequired,
};

TenantAdminRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

SystemAdminRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

PublicOnlyRoute.propTypes = {
  children: PropTypes.node.isRequired,
};
